import type { CatalogMoveConflictResolution } from "@app/commands/catalog/move";
import type { Router } from "@core/Api/Router";
import type CustomArticle from "@core/SitePresenter/customArticles/model/CustomArticle";
import type { ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import { getNewCommitOidFromPathname } from "@ext/git/actions/Revisions/logic/utils/getCommitOidFromPathname";
import type { GcOptions, TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import type { DiffItemContentScope } from "@ext/git/core/GitDiffItemCreator/DiffItemContent/DiffItemContent";
import type UiLanguage from "@ext/localization/core/model/Language";
import type { ContentLanguage } from "@ext/localization/core/model/Language";
import type { Syntax } from "@ext/markdown/core/edit/logic/Formatter/Formatters/typeFormats/model/Syntax";
import type { ArticleLanguage } from "@ext/serach/modulith/SearchArticle";
import type { ResourceFilter } from "@ext/serach/Searcher";
import type { SearcherType } from "@ext/serach/SearcherManager";
import type { LevelName } from "@ext/settings/logic/settings";
import type SourceType from "@ext/storage/logic/SourceDataProvider/model/SourceType";
import type Theme from "@ext/Theme/Theme";
import { ExportFormat } from "@ext/wordExport/components/ItemExport";
import type { WorkspacePath } from "@ext/workspace/WorkspaceConfig";
import type MimeTypes from "./Types/MimeTypes";
import Url from "./Types/Url";

/**
 * @deprecated Consider using `useApi(..)` hook instead
 */
export default class ApiUrlCreator {
	constructor(
		protected _basePath: string,
		protected _catalogName?: string,
		private _articlePath?: string,
		private _providerType?: ArticleProviderType,
	) {}

	get articlePath() {
		return this._articlePath;
	}

	get catalogName() {
		return this._catalogName;
	}

	fromArticle(articlePath: string) {
		return this.fromNewArticlePath(`${this._catalogName}/${articlePath}`);
	}

	fromNewArticlePath(articlePath: string, catalogName?: string) {
		return new ApiUrlCreator(this._basePath, catalogName || this._catalogName, articlePath);
	}

	public saveWorkspaceSections() {
		return Url.fromBasePath(`/api/workspace/saveSections`, this._basePath);
	}

	public switchWorkspace(id: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/switch`, this._basePath, { id });
	}

	public editWorkspace() {
		return Url.fromBasePath(`/api/workspace/edit`, this._basePath);
	}

	public removeWorkspace(id: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/remove`, this._basePath, { id });
	}

	public createWorkspace() {
		return Url.fromBasePath(`/api/workspace/create`, this._basePath, {});
	}

	public getCustomStyleAsset(workspacePath?: WorkspacePath) {
		return Url.fromBasePath(
			`/api/workspace/assets/getCustomStyle`,
			this._basePath,
			workspacePath ? { path: workspacePath } : {},
		);
	}

	public getPlugins(workspacePath: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/assets/plugin/getAll`, this._basePath, { path: workspacePath });
	}
	public addPlugin(workspacePath: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/assets/plugin/add`, this._basePath, {
			id: workspacePath,
		});
	}
	public deletePlugin(workspacePath: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/assets/plugin/delete`, this._basePath, {
			path: workspacePath,
		});
	}
	public updateHomeLogo(workspacePath: WorkspacePath, theme: Theme) {
		return Url.fromBasePath("/api/workspace/assets/homeLogo/update", this._basePath, {
			theme,
			path: workspacePath,
		});
	}

	public deleteHomeLogo(workspacePath: WorkspacePath, theme: Theme) {
		return Url.fromBasePath("/api/workspace/assets/homeLogo/delete", this._basePath, {
			theme,
			path: workspacePath,
		});
	}

	public getHomeLogo(workspacePath: WorkspacePath, theme: Theme) {
		return Url.fromBasePath("/api/workspace/assets/homeLogo/get", this._basePath, { theme, path: workspacePath });
	}

	public getDocportalHomeLogo(theme: Theme, isMobile = false) {
		return Url.fromBasePath("/api/home-logo", this._basePath, { theme, isMobile: isMobile.toString() });
	}

	public setCustomStyleAsset(workspacePath: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/assets/setCustomStyle`, this._basePath, { workspacePath });
	}

	public setDefaultPath(path: string) {
		return Url.fromBasePath(`/api/workspace/setDefaultPath`, this._basePath, { path });
	}

	public getArticleResource(
		src: string,
		mimeType?: MimeTypes,
		catalogName?: string,
		itemId?: string,
		providerType?: ArticleProviderType,
		scope?: string,
	) {
		const isFragment = providerType === "fragment";
		return Url.fromBasePath(`/api/article/resource/get`, this._basePath, {
			catalogName: catalogName ?? this._catalogName,
			articlePath: itemId || this._articlePath,
			mimeType,
			src,
			providerType,
			scope,
			// A reader of this article may read the files of a fragment it shows, though not the catalog they come from.
			hostCatalogName: isFragment ? this._catalogName : undefined,
			hostArticlePath: isFragment ? this._articlePath : undefined,
		});
	}

	public getResourceByPath(fullResourcePath: string) {
		return Url.fromBasePath(`/api/article/resource/getByPath`, this._basePath, {
			fullResourcePath,
			catalogName: this._catalogName,
		});
	}

	public setSourceData() {
		return Url.fromBasePath(`/api/storage/sourceData/setSourceData`, this._basePath);
	}

	public getSourceData() {
		return Url.fromBasePath(`/api/storage/sourceData/getSourceData`, this._basePath);
	}

	public getSourceHealthcheck(sourceName: string) {
		return Url.fromBasePath(`/api/storage/sourceData/healthcheck`, this._basePath, {
			sourceName,
		});
	}

	public getSourceDataUsage(sourceName: string) {
		return Url.fromBasePath(`/api/storage/getSourceDataUsage`, this._basePath, { sourceName });
	}

	public getStorageStats(catalogName: string) {
		return Url.fromBasePath(`/api/storage/getStorageStats`, this._basePath, { catalogName });
	}

	public runGc(opts: GcOptions) {
		return Url.fromBasePath(`/api/storage/runGc`, this._basePath, {
			catalogName: this._catalogName,
			looseObjectsLimit: opts.looseObjectsLimit?.toString(),
			packFilesLimit: opts.packFilesLimit?.toString(),
		});
	}

	public lfsPrune() {
		return Url.fromBasePath(`/api/storage/lfsPrune`, this._basePath, { catalogName: this._catalogName });
	}

	public removeSourceData(sourceName: string) {
		return Url.fromBasePath(`/api/storage/removeSourceData`, this._basePath, { sourceName });
	}

	public initStorage(sendArticlePath = true, catalogName?: string) {
		const params: Record<string, string> = { catalogName: catalogName ?? this._catalogName };
		if (sendArticlePath) params.articlePath = this._articlePath;

		return Url.fromBasePath(`/api/storage/init`, this._basePath, params);
	}

	public getArticleContentByRelativePath(articleRelativePath: string) {
		return Url.fromBasePath(`/api/article/features/getRenderContent`, this._basePath, {
			articleRelativePath,
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getArticleEditorContent() {
		return Url.fromBasePath(`/api/article/features/getEditorContent`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getArticleRenderDataByLogicPath(logicPath: string) {
		return Url.fromBasePath(`/api/article/features/getRenderContentByLogicPath`, this._basePath, {
			catalogName: this._catalogName,
			logicPath,
		});
	}

	public getArticleHeadersByRelativePath(articleRelativePath: string) {
		return Url.fromBasePath(`/api/article/features/getArticleHeadersByRelativePath`, this._basePath, {
			articleRelativePath,
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public setArticleResource(src: string, articlePath?: string, providerType?: ArticleProviderType, force?: boolean) {
		return Url.fromBasePath(`/api/article/resource/set`, this._basePath, {
			articlePath: articlePath ?? this._articlePath,
			catalogName: this._catalogName,
			src,
			providerType,
			force: force ? "true" : undefined,
		});
	}

	public deleteArticleResource(src: string, itemId?: string, providerType?: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/resource/remove`, this._basePath, {
			articlePath: itemId || this._articlePath,
			catalogName: this._catalogName,
			src,
			providerType,
		});
	}

	public getArticleFileBrotherNames(itemId?: string, providerType?: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/features/getBrotherNames`, this._basePath, {
			articlePath: itemId || this._articlePath,
			catalogName: this._catalogName,
			providerType,
		});
	}

	public getCommentsByAuthors() {
		return Url.fromBasePath(`/api/comments/getCommentsByAuthors`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public searchReviewItems() {
		return Url.fromBasePath(`/api/review/search`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getNewCommentId() {
		return Url.fromBasePath(`/api/comments/getNewCommentId`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public deleteComment(id: string, articlePath?: string) {
		return Url.fromBasePath(`/api/comments/delete`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: articlePath || this._articlePath,
			id,
		});
	}

	public getAuthAssertUrl() {
		return Url.fromBasePath("/api/auth/assert", this._basePath);
	}

	public getSendMailTokenUrl(mail: string) {
		return Url.fromBasePath("/api/auth/sendMailToken", this._basePath, { mail });
	}

	public getFileUrl(url: string) {
		return Url.fromBasePath("/api/load/", this._basePath) + url;
	}

	public getDbDiagramUrl(path: string, primary: string, tags: string, draw?: string) {
		return Url.fromBasePath("/api/dbDiagram", this._basePath, {
			path: path,
			tags: tags,
			draw: draw,
			primary: primary,
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getEditOnSourceLink(articlePath: string) {
		return Url.fromBasePath("/api/article/editOn/source", this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getEditOnAppUrl(articlePath: string) {
		return Url.fromBasePath("/api/article/editOn/app", this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getLogoUrl(catalogName: string, theme: Theme, force?: boolean) {
		return Url.fromBasePath(`/api/catalog/logo`, this._basePath, {
			catalogName,
			theme,
			force: force?.toString(),
		});
	}
	public catalogLogoExist(catalogName: string, theme: Theme) {
		return Url.fromBasePath(`/api/catalog/logo/exist`, this._basePath, {
			catalogName,
			theme,
		});
	}

	public updateCatalogLogo(catalogName: string, name: string) {
		return Url.fromBasePath(`/api/catalog/logo/update`, this._basePath, {
			catalogName,
			name,
		});
	}

	public deleteCatalogLogo(catalogName: string, theme: Theme) {
		return Url.fromBasePath(`/api/catalog/logo/delete`, this._basePath, {
			catalogName,
			theme,
		});
	}

	public getAuthUrl(router: Router, isLogged: boolean) {
		const from = encodeURIComponent(router?.basePath + router?.path);
		return Url.fromBasePath(isLogged ? `/api/auth/logout` : `/api/auth/login`, this._basePath, {
			from,
		});
	}

	public saveEnterpriseWorkspaceSections() {
		return Url.fromBasePath(`api/enterprise/saveSections`, this._basePath);
	}

	public saveEnterpriseCloudWorkspaceSections() {
		return Url.fromBasePath(`api/enterpriseCloud/saveSections`, this._basePath);
	}

	public getAddEnterpriseWorkspaceUrl(oneTimeCode: string) {
		return Url.fromBasePath(`api/enterprise/addWorkspace`, this._basePath, { oneTimeCode });
	}

	public getAddEnterpriseCloudWorkspaceUrl() {
		return Url.fromBasePath(`api/enterpriseCloud/addWorkspace`, this._basePath);
	}

	public getInitEnterpriseCloudCatalogUrl(repositoryName: string) {
		return Url.fromBasePath(`api/enterpriseCloud/initNewCatalog`, this._basePath, {
			repositoryName,
		});
	}

	public getPrepareEnterpriseCloudCatalogUrl(
		oldCatalogName: string,
		newCatalogTitle: string,
		newRepositoryName: string,
	) {
		return Url.fromBasePath(`api/enterpriseCloud/prepareNewCatalog`, this._basePath, {
			oldCatalogName,
			newCatalogTitle,
			newRepositoryName,
		});
	}

	public getEnterpriseCloudCatalogRepositoryNameUrl(oldCatalogName: string, catalogTitle: string) {
		return Url.fromBasePath(`api/enterpriseCloud/getCatalogRepositoryName`, this._basePath, {
			oldCatalogName,
			catalogTitle,
		});
	}

	public getDisableCloudUrl() {
		return Url.fromBasePath(`api/enterpriseCloud/disableCloud`, this._basePath);
	}

	public getEnableCloudUrl() {
		return Url.fromBasePath(`api/enterpriseCloud/enableCloud`, this._basePath);
	}

	public getLogoutEnterpriseUrl(id: WorkspacePath) {
		return Url.fromBasePath(`api/enterprise/logout`, this._basePath, { id });
	}

	public getLogoutGesCloudUrl(id: WorkspacePath) {
		return Url.fromBasePath(`api/enterpriseCloud/logout`, this._basePath, { id });
	}

	public getSyncGesCloudWorkspaceUrl() {
		return Url.fromBasePath(`api/enterpriseCloud/syncWorkspace`, this._basePath);
	}

	public getCloneEnterpriseCatalogsUrl() {
		return Url.fromBasePath(`api/enterprise/cloneCatalogs`, this._basePath);
	}

	public getCheckEditEnterpriseWorkspaceUrl(workspaceId: WorkspacePath) {
		return Url.fromBasePath(`api/enterprise/checkEditWorkspace`, this._basePath, { workspaceId });
	}

	public refreshAvailableCatalogs() {
		return Url.fromBasePath(`api/enterprise/refreshAvailableCatalogs`, this._basePath);
	}

	public refreshEnterpriseWorkspace() {
		return Url.fromBasePath(`api/enterprise/refreshWorkspace`, this._basePath);
	}

	public setGesUrl(gesUrl: string) {
		return Url.fromBasePath(`api/enterprise/setGesUrl`, this._basePath, { gesUrl });
	}

	public setGesCloudUrl(gesCloudUrl: string) {
		return Url.fromBasePath(`api/enterpriseCloud/setGesCloudUrl`, this._basePath, { gesCloudUrl });
	}

	public switchGesCloudOrganization(redirectUrl: string, organizationId: string) {
		return Url.fromBasePath(`api/enterpriseCloud/switchOrganization`, this._basePath, {
			redirectUrl,
			organizationId,
		});
	}

	public getAuthSsoUrl(data: string, sign: string, from: string) {
		return Url.fromBasePath(`api/auth/sso`, this._basePath, { data, sign, from });
	}

	public getPrintableContentUrl(catalogName: string, isCategory?: boolean, itemPath?: string, titleNumber?: boolean) {
		return Url.fromBasePath(`/api/item/getPrintableContent`, this._basePath, {
			catalogName,
			isCategory: isCategory?.toString?.(),
			itemPath,
			titleNumber: titleNumber?.toString(),
		});
	}

	public getPdfSaveUrl(isCategory: boolean, itemPath?: string) {
		return Url.fromBasePath(`/api/pdf`, this._basePath, {
			itemPath,
			catalogName: this._catalogName,
			isCategory: isCategory?.toString(),
		});
	}

	public getPdfTemplateUrl(name: string) {
		return Url.fromBasePath(`/api/pdf/template`, this._basePath, {
			name,
		});
	}

	public getWordSaveUrl(isCategory: boolean, itemPath?: string, wordTemplateName?: string) {
		return Url.fromBasePath(`/api/word`, this._basePath, {
			itemPath,
			catalogName: this._catalogName,
			isCategory: isCategory ? "true" : "false",
			wordTemplateName,
		});
	}

	public getErrorWordElementsUrl(isCategory: boolean, itemPath?: string) {
		return Url.fromBasePath(`/api/word/getErrorElements`, this._basePath, {
			itemPath,
			catalogName: this._catalogName,
			isCategory: isCategory ? "true" : "false",
			exportFormat: ExportFormat.docx,
		});
	}

	public getUnsupportedElementsUrl(storageDataName: string, sourceType: SourceType) {
		return Url.fromBasePath(`/api/storage/import/getUnsupportedElements`, this._basePath, {
			storageDataName,
			sourceType,
		});
	}

	public getVersionControlResetBranchesUrl() {
		return Url.fromBasePath("/api/versionControl/branch/reset", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public resetFileLock() {
		return Url.fromBasePath(`/api/storage/resetFileLock`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getSyncCountUrl({
		forceCatalogName,
	}: {
		forceCatalogName?: string;
		fetch?: boolean;
		returnError?: boolean;
	} = {}) {
		return Url.fromBasePath(`/api/storage/getSyncCount`, this._basePath, {
			catalogName: forceCatalogName ?? this._catalogName,
		});
	}

	public getAllSyncCountUrl(shouldFetch: boolean, resetSyncCount: boolean) {
		return Url.fromBasePath(`/api/storage/getAllSyncCount`, this._basePath, {
			fetch: shouldFetch.toString(),
			resetSyncCount: resetSyncCount.toString(),
		});
	}

	public getAllSyncableWorkspacesUrl() {
		return Url.fromBasePath(`/api/storage/getAllSyncableWorkspaces`, this._basePath, {});
	}

	public getStoragePublishUrl() {
		return Url.fromBasePath(`/api/storage/publish`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getStoragePublishHealthcheckUrl() {
		return Url.fromBasePath(`/api/storage/publishHealthcheck`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getVersionControlDiscardUrl(articlePath?: string) {
		return Url.fromBasePath(`/api/versionControl/discard`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getVersionControlDiffTreeUrl(oldScope: TreeReadScope, newScope?: TreeReadScope) {
		return Url.fromBasePath(`/api/versionControl/diff/getDiffTree`, this._basePath, {
			catalogName: this._catalogName,
			oldScope: JSON.stringify(oldScope),
			newScope: newScope ? JSON.stringify(newScope) : undefined,
		});
	}

	public getVersionControlRevisionsUrl(from?: string, depth?: number) {
		return Url.fromBasePath(`/api/versionControl/revision/getRevisions`, this._basePath, {
			catalogName: this._catalogName,
			from,
			depth: depth?.toString(),
		});
	}

	public getArticleDiffUrl(scope?: string, oldScope?: string) {
		return Url.fromBasePath(`/api/versionControl/diff/getArticleDiff`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			scope,
			oldScope,
		});
	}

	public getVersionControlDiffItemContentUrl(scope: DiffItemContentScope, filePath: string, isResource?: boolean) {
		return Url.fromBasePath(`/api/versionControl/diff/getDiffItemContent`, this._basePath, {
			catalogName: this._catalogName,
			scope: JSON.stringify(scope),
			filePath,
			isResource: isResource?.toString(),
		});
	}

	public getVersionControlStatuses(includeCommitOid = true) {
		return Url.fromBasePath(`/api/versionControl/statuses`, this._basePath, {
			catalogName: this._catalogName,
			commitOid: includeCommitOid ? getNewCommitOidFromPathname(this._catalogName) : undefined,
		});
	}

	public getVersionControlFileStatus(articlePath: string) {
		return Url.fromBasePath(`/api/versionControl/fileStatus`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getStorageFetch() {
		return Url.fromBasePath(`/api/storage/fetch`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getStorageHaveToPull(shouldFetch = true) {
		return Url.fromBasePath(`/api/storage/haveToPull`, this._basePath, {
			catalogName: this._catalogName,
			shouldFetch: shouldFetch.toString(),
		});
	}

	public getStorageCanPull() {
		return Url.fromBasePath(`/api/storage/canPull`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getStorageSyncUrl() {
		return Url.fromBasePath(`/api/storage/sync`, this._basePath, { catalogName: this._catalogName });
	}

	public markRepositoryAsBroken(message?: string) {
		return Url.fromBasePath(`/api/storage/markAsBroken`, this._basePath, {
			catalogName: this._catalogName,
			message,
		});
	}

	public startRecover() {
		return Url.fromBasePath(`/api/storage/startRecover`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public startClone(
		path: string,
		isBare = false,
		skipCheck?: boolean,
		branch?: string,
		deleteIfExists?: boolean,
		skipLfsPull?: boolean,
	) {
		return Url.fromBasePath(`/api/storage/startClone`, this._basePath, {
			branch,
			skipCheck: skipCheck?.toString(),
			path,
			isBare: isBare?.toString(),
			deleteIfExists: deleteIfExists?.toString(),
			skipLfsPull: skipLfsPull?.toString(),
		});
	}

	public getRemoveCloneCatalogUrl(name: string) {
		return Url.fromBasePath(`/api/storage/removeCloneCatalog`, this._basePath, {
			catalogName: name,
		});
	}

	public getCloneProgress(path: string) {
		return Url.fromBasePath(`/api/storage/getCloneProgress`, this._basePath, {
			path,
		});
	}

	public cancelClone(path: string) {
		return Url.fromBasePath(`/api/storage/cancelClone`, this._basePath, {
			path,
		});
	}

	public getStorageUrl() {
		return Url.fromBasePath("/api/storage/getUrl", this._basePath, { catalogName: this._catalogName });
	}

	public getLfsOptions() {
		return Url.fromBasePath("/api/versionControl/lfs/getLfsOptions", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public updateLfsOptions() {
		return Url.fromBasePath("/api/versionControl/lfs/updateLfsOptions", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public checkWorkspaceLfsDivergence() {
		return Url.fromBasePath("/api/versionControl/lfs/checkWorkspaceLfsDivergence", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getWorkspaceLfsMigrationStats() {
		return Url.fromBasePath("/api/versionControl/lfs/getWorkspaceLfsMigrationStats", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public applyWorkspaceLfsMigration() {
		return Url.fromBasePath("/api/versionControl/lfs/applyWorkspaceLfsMigration", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getAttachmentsMigrationStats() {
		return Url.fromBasePath("/api/versionControl/lfs/getAttachmentsMigrationStats", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public enableAutoLfsAttachments() {
		return Url.fromBasePath("/api/versionControl/lfs/enableAutoLfsAttachments", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getGitCommitAuthors(authorFilter?: string) {
		return Url.fromBasePath("/api/versionControl/getCommitAuthors", this._basePath, {
			catalogName: this._catalogName,
			authorFilter,
		});
	}

	public getGitCommitRange() {
		return Url.fromBasePath("/api/versionControl/getCommitRange", this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getCurrentBranch(
		{
			onlyName = true,
			cached = true,
			cachedMergeRequests = true,
		}: {
			onlyName?: boolean;
			cached?: boolean;
			cachedMergeRequests?: boolean;
		} = { cached: true, cachedMergeRequests: true, onlyName: true },
	) {
		return Url.fromBasePath(`/api/versionControl/branch/get`, this._basePath, {
			catalogName: this._catalogName,
			cached: cached.toString(),
			onlyName: onlyName.toString(),
			cachedMergeRequests: cachedMergeRequests.toString(),
		});
	}

	public getVersionControlBranchToCheckoutUrl() {
		return Url.fromBasePath(`/api/versionControl/branch/getBranchToCheckout`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getVersionControlAbortCheckoutStateUrl() {
		return Url.fromBasePath(`/api/versionControl/branch/abortCheckoutState`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getVersionControlCheckoutBranchUrl(branch: string, transferChanges = true) {
		return Url.fromBasePath(`/api/versionControl/branch/checkout`, this._basePath, {
			branch: encodeURIComponent(branch),
			transferChanges: String(transferChanges),
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getVersionControlCreateNewBranchUrl(branch: string) {
		return Url.fromBasePath(`/api/versionControl/branch/create`, this._basePath, {
			branch: encodeURIComponent(branch),
			catalogName: this._catalogName,
		});
	}

	public getVersionControlDeleteBranchUrl(branch: string) {
		return Url.fromBasePath(`/api/versionControl/branch/delete`, this._basePath, {
			branch: encodeURIComponent(branch),
			catalogName: this._catalogName,
		});
	}

	public getShareLinkUrl(filePath: string) {
		return Url.fromBasePath(`/api/catalog/share/getShareLink`, this._basePath, {
			catalogName: this._catalogName,
			filePath,
		});
	}

	public getShareLinkDataUrl() {
		return Url.fromBasePath(`/api/catalog/share/getShareLinkData`, this._basePath);
	}

	public getHealthcheckUrl() {
		return Url.fromBasePath(`/api/healthcheck`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getSearchDataUrl(
		query: string | undefined,
		catalogName?: string,
		type?: SearcherType,
		articlesLanguage?: ArticleLanguage,
	) {
		return Url.fromBasePath(`/api/search/searchCommand`, this._basePath, {
			query,
			catalogName,
			type,
			articlesLanguage,
		});
	}

	public getSearchChatUrl(
		query: string,
		catalogName: string | undefined,
		articlesLanguage: ArticleLanguage | undefined,
		responseLanguage: ContentLanguage | undefined,
		articleRefFilter: string | undefined,
	) {
		return Url.fromBasePath(`/api/search/chat`, this._basePath, {
			query,
			catalogName,
			articlesLanguage,
			responseLanguage,
			currentArticle: this._articlePath,
			articleRefFilter,
		});
	}

	public getSearchChatAvailableUrl() {
		return Url.fromBasePath(`/api/search/chatAvailable`, this._basePath);
	}

	public getIndexingProgressUrl(resourceFilter?: ResourceFilter) {
		return Url.fromBasePath(`/api/search/getIndexingProgress`, this._basePath, {
			resourceFilter,
		});
	}

	public getResetSearchDataUrl(catalogName?: string) {
		return Url.fromBasePath(`/api/search/resetSearchData`, this._basePath, {
			catalogName,
		});
	}

	public getAgentSessionCreateUrl() {
		return Url.fromBasePath(`/api/agent/session/create`, this._basePath, {});
	}

	public getAgentSessionStateUrl(sessionId: string) {
		return Url.fromBasePath(`/api/agent/session/state`, this._basePath, { sessionId });
	}

	public getAgentSessionAttachmentUrl(sessionId: string, storagePath: string) {
		return Url.fromBasePath(`/api/agent/session/attachment`, this._basePath, { sessionId, storagePath });
	}

	public getAgentSessionUploadAttachmentUrl(sessionId: string, name: string, mime: string) {
		return Url.fromBasePath(`/api/agent/session/uploadAttachment`, this._basePath, { sessionId, name, mime });
	}

	public getAgentSessionDeleteAttachmentUrl(sessionId: string, storagePath: string) {
		return Url.fromBasePath(`/api/agent/session/deleteAttachment`, this._basePath, { sessionId, storagePath });
	}

	public getAgentSessionContextUrl(sessionId: string) {
		return Url.fromBasePath(`/api/agent/session/context`, this._basePath, { sessionId });
	}

	public getAgentSessionListUrl() {
		return Url.fromBasePath(`/api/agent/session/list`, this._basePath, {});
	}

	public getAgentSessionCancelUrl(sessionId: string) {
		return Url.fromBasePath(`/api/agent/session/cancel`, this._basePath, { sessionId });
	}

	public getAgentSessionDeleteUrl(sessionId: string) {
		return Url.fromBasePath(`/api/agent/session/delete`, this._basePath, { sessionId });
	}

	public getAgentSessionRenameUrl() {
		return Url.fromBasePath(`/api/agent/session/rename`, this._basePath, {});
	}

	public getAgentSessionRestoreUrl() {
		return Url.fromBasePath(`/api/agent/session/restore`, this._basePath, {});
	}

	public getAgentSessionDraftUrl(sessionId: string | null) {
		return Url.fromBasePath(`/api/agent/session/draft`, this._basePath, { sessionId: sessionId ?? "" });
	}

	public getAgentSessionSaveDraftUrl() {
		return Url.fromBasePath(`/api/agent/session/saveDraft`, this._basePath, {});
	}

	public getAgentMessageSendUrl() {
		return Url.fromBasePath(`/api/agent/message/send`, this._basePath, {});
	}

	public getAgentBrowserRevealUrl(sessionId: string) {
		return Url.fromBasePath(`/api/agent/browser/reveal`, this._basePath, { sessionId });
	}

	public getAgentBrowserSetAllowedUrl() {
		return Url.fromBasePath(`/api/agent/browser/setBrowserAllowed`, this._basePath, {});
	}

	public getAgentDocportalHealthcheckUrl() {
		return Url.fromBasePath(`/api/agent/docportal/healthcheck`, this._basePath, {});
	}

	public getAgentSkillsListUrl(catalogName: string | null) {
		return Url.fromBasePath(`/api/agent/skills/list`, this._basePath, { catalogName });
	}

	public getAgentSecretsListUrl(includeValues?: boolean) {
		return Url.fromBasePath(
			`/api/agent/secrets/list`,
			this._basePath,
			includeValues ? { includeValues: "true" } : {},
		);
	}

	public getAgentSecretsSetUrl() {
		return Url.fromBasePath(`/api/agent/secrets/set`, this._basePath, {});
	}

	public getAgentSecretsDeleteUrl(key: string) {
		return Url.fromBasePath(`/api/agent/secrets/delete`, this._basePath, { key });
	}

	public getAgentSecretsUpdateUrl() {
		return Url.fromBasePath(`/api/agent/secrets/update`, this._basePath, {});
	}

	public mergeInto(branchName: string, deleteAfterMerge?: boolean, squash?: boolean) {
		return Url.fromBasePath(`/api/versionControl/branch/mergeInto`, this._basePath, {
			catalogName: this._catalogName,
			branchName,
			deleteAfterMerge: deleteAfterMerge?.toString(),
			squash: squash?.toString(),
		});
	}

	public getMergeData() {
		return Url.fromBasePath(`/api/versionControl/mergeConflict/getMergeData`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public abortMerge() {
		return Url.fromBasePath(`/api/versionControl/mergeConflict/abort`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public resolveMerge() {
		return Url.fromBasePath(`/api/versionControl/mergeConflict/resolve`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public validateMerge() {
		return Url.fromBasePath(`/api/versionControl/mergeConflict/validateMerge`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public createMergeRequest() {
		return Url.fromBasePath(`/api/mergeRequests/create`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public deleteMergeRequest() {
		return Url.fromBasePath(`/api/mergeRequests/deleteMr`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public mergeRequestMerge(validateMerge?: boolean) {
		return Url.fromBasePath(`/api/mergeRequests/merge`, this._basePath, {
			catalogName: this._catalogName,
			validateMerge: validateMerge?.toString(),
		});
	}

	public getVideoUrl(path: string) {
		return Url.fromBasePath("/api/elements/video/getUrl", this._basePath, {
			path: path,
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getSetLanguageURL(language: UiLanguage) {
		return Url.fromBasePath("/api/lang/set", this._basePath, { language });
	}

	public getUpdateSettingURL(level: LevelName = "app", workspacePath?: string) {
		return Url.fromBasePath("/api/settings/update", this._basePath, { level, workspacePath });
	}

	public getResetSettingURL(level: LevelName = "app") {
		return Url.fromBasePath("/api/settings/reset", this._basePath, { level });
	}

	public createCatalog() {
		return Url.fromBasePath(`/api/catalog/create`, this._basePath);
	}

	public removeCatalog(name?: string) {
		return Url.fromBasePath(`/api/catalog/remove`, this._basePath, {
			catalogName: name || this._catalogName,
		});
	}

	public getArticleNameAfterMove(
		articlePath: string,
		targetWorkspacePath: WorkspacePath,
		targetCatalogName: string,
		sourceCatalogName: string,
		createNewCatalog?: boolean,
	) {
		return Url.fromBasePath(`/api/article/getNameAfterMove`, this._basePath, {
			articlePath,
			targetWorkspacePath,
			targetCatalogName,
			sourceCatalogName,
			createNewCatalog: createNewCatalog ? "true" : "false",
		});
	}

	public getCatalogNameAfterMove(desiredName: string, to: WorkspacePath) {
		return Url.fromBasePath(`/api/catalog/getNameAfterMove`, this._basePath, {
			desiredName,
			to,
		});
	}

	public moveCatalog(
		targetWorkspacePath: WorkspacePath,
		sourceCatalogName: string,
		targetCatalogName: string,
		conflictResolution: CatalogMoveConflictResolution,
		sourceCatalogAddPostfix?: string,
	) {
		return Url.fromBasePath(`/api/catalog/move`, this._basePath, {
			targetWorkspacePath,
			sourceCatalogName,
			targetCatalogName,
			conflictResolution,
			sourceCatalogAddPostfix,
		});
	}

	public getWorkspaceCatalogList(path: WorkspacePath) {
		return Url.fromBasePath(`/api/workspace/getUninitializedCatalogList`, this._basePath, {
			workspacePath: path,
		});
	}

	public addCatalogLanguage(code: ContentLanguage) {
		return Url.fromBasePath(`/api/catalog/language/add`, this._basePath, {
			catalogName: this._catalogName,
			code,
		});
	}

	public removeCatalogFromSpace(name?: string, repo?: string) {
		return Url.fromBasePath(`/api/enterprise/removeFromSpace`, this._basePath, {
			catalogName: name || this._catalogName,
			repo,
		});
	}

	public removeCatalogLanguage(code: ContentLanguage) {
		return Url.fromBasePath(`/api/catalog/language/remove`, this._basePath, {
			catalogName: this._catalogName,
			code,
		});
	}

	public updateCatalogProps() {
		return Url.fromBasePath(`/api/catalog/updateProps`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getCatalogProps(catalogName?: string) {
		return Url.fromBasePath(`/api/catalog/getProps`, this._basePath, {
			catalogName: catalogName || this._catalogName,
		});
	}

	public updateCatalogNav(itemPath: string) {
		return Url.fromBasePath(`/api/catalog/updateNavigation`, this._basePath, {
			itemPath,
			catalogName: this._catalogName,
		});
	}

	public getCatalogBrotherFileNames() {
		return Url.fromBasePath(`/api/catalog/getBrotherFileNames`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public createArticle(parentPath?: string, afterPath?: string) {
		return Url.fromBasePath(`/api/article/create`, this._basePath, {
			catalogName: this._catalogName,
			parentPath,
			afterPath,
		});
	}

	public getItemProps(itemPath: string) {
		return Url.fromBasePath(`/api/article/getProps`, this._basePath, {
			catalogName: this._catalogName,
			path: itemPath,
		});
	}

	public getNotifications(itemPath: string) {
		return Url.fromBasePath(`/api/enterprise/notifications/get`, this._basePath, {
			catalogName: this._catalogName,
			path: itemPath,
		});
	}

	public updateNotifications(itemPath: string) {
		return Url.fromBasePath(`/api/enterprise/notifications/update`, this._basePath, {
			catalogName: this._catalogName,
			path: itemPath,
		});
	}

	public updateArticleContent() {
		return Url.fromBasePath(`/api/article/updateContent`, this._basePath, {
			path: this._articlePath,
			catalogName: this._catalogName,
		});
	}

	public setArticleContent(articlePath: string, rawMarkdown?: boolean) {
		return Url.fromBasePath(`/api/article/features/setContent`, this._basePath, {
			path: articlePath,
			catalogName: this._catalogName,
			rawMarkdown: rawMarkdown?.toString(),
		});
	}

	public getArticleContent(articlePath: string) {
		return Url.fromBasePath(`/api/article/features/getContent`, this._basePath, {
			path: articlePath,
			catalogName: this._catalogName,
		});
	}

	public getLinkItems(catalogName?: string) {
		return Url.fromBasePath(`/api/article/features/getLinkItems`, this._basePath, {
			path: this._articlePath,
			catalogName: catalogName || this._catalogName,
			currentCatalogName: this._catalogName,
			providerType: this._providerType,
		});
	}

	public getLinkItemByPath(path: string, catalogName?: string) {
		return Url.fromBasePath(`/api/article/features/getLinkItemByPath`, this._basePath, {
			path: encodeURIComponent(path),
			catalogName: catalogName || this._catalogName,
		});
	}

	public setLastVisitedArticle() {
		return Url.fromBasePath(`/api/page/setLastVisitedArticle`, this._basePath, { catalogName: this._catalogName });
	}

	public getArticlePageData(articlePath?: string, catalogName?: string) {
		return Url.fromBasePath(`/api/page/getArticlePageData`, this._basePath, {
			path: articlePath || this._articlePath,
			catalogName: catalogName || this._catalogName,
		});
	}

	public getArticleTakenAliases(itemPath: string) {
		return Url.fromBasePath(`/api/article/features/getTakenAliases`, this._basePath, {
			path: itemPath,
			catalogName: this._catalogName,
		});
	}

	public getArticleBrotherFileNames(itemPath: string, fileName?: string, newFileName?: string) {
		return Url.fromBasePath(`/api/article/features/getBrotherFileNames`, this._basePath, {
			path: itemPath,
			catalogName: this._catalogName,
			fileName,
			newFileName,
		});
	}

	public getCustomArticle(name: CustomArticle) {
		return Url.fromBasePath("/api/article/features/getCustomArticle", this._basePath, { name });
	}

	public removeItem(articlePath: string) {
		return Url.fromBasePath(`/api/item/remove`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: articlePath,
			currentArticlePath: this._articlePath,
		});
	}

	public updateItemProps(articlePath?: string, fileName?: string) {
		return Url.fromBasePath(`/api/item/updateProps`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
			fileName,
		});
	}

	public getPermissionValuesUrl(path?: string) {
		return Url.fromBasePath(`/api/item/getPermission`, this._basePath, {
			catalogName: this._catalogName,
			path,
		});
	}

	public getShareTicket(group: string, date: string) {
		return Url.fromBasePath(`/api/catalog/share/getShareTicket`, this._basePath, {
			catalogName: this._catalogName,
			path: this._articlePath,
			group,
			date,
		});
	}

	public setPermissionValuesUrl(path?: string) {
		return Url.fromBasePath(`/api/item/setPermission`, this._basePath, {
			catalogName: this._catalogName,
			path,
		});
	}

	public getArticlesWithFragment(fragmentId: string) {
		return Url.fromBasePath(`/api/elements/fragment/getArticlesWithFragment`, this._basePath, {
			catalogName: this._catalogName,
			fragmentId,
		});
	}

	public clearArticlesContentWithFragment(fragmentId: string) {
		return Url.fromBasePath(`/api/elements/fragment/clearArticlesContent`, this._basePath, {
			catalogName: this._catalogName,
			fragmentId,
		});
	}

	public getFragmentRenderData(fragmentId: string, scope?: string) {
		return Url.fromBasePath(`/api/elements/fragment/getRenderData`, this._basePath, {
			catalogName: this._catalogName,
			fragmentId,
			scope,
		});
	}

	public createCustomIcon() {
		return Url.fromBasePath(`/api/elements/icon/create`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public deleteCustomIcon(code: string) {
		return Url.fromBasePath(`/api/elements/icon/delete`, this._basePath, {
			catalogName: this._catalogName,
			code,
		});
	}

	public getCustomIconsList() {
		return Url.fromBasePath(`/api/elements/icon/getIconsList`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public createResourceFromPath(resourcePath: string, resourceName: string) {
		return Url.fromBasePath(`/api/article/resource/createFromPath`, this._basePath, {
			articlePath: this._articlePath,
			catalogName: this._catalogName,
			resourcePath,
			resourceName,
		});
	}

	public getViewRenderData() {
		return Url.fromBasePath(`/api/catalog/view/getRenderData`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getPropertyUsages(propertyName: string, value: string) {
		return Url.fromBasePath(`/api/article/property/get`, this._basePath, {
			catalogName: this._catalogName,
			propertyName,
			value,
			articlePath: this._articlePath,
		});
	}

	public updateArticleProperty(articlePath: string, propertyName: string, newValue: string, isDelete?: string) {
		return Url.fromBasePath(`/api/article/property/update`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
			propertyName,
			newValue,
			isDelete,
		});
	}

	public setArticleProperty(articlePath: string, propertyName: string) {
		return Url.fromBasePath(`/api/article/property/set`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
			propertyName,
		});
	}

	public moveArticleProperty(articlePath: string, propertyName: string) {
		return Url.fromBasePath(`/api/article/property/move`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
			propertyName,
		});
	}

	public removePropertyFromArticles(propertyName: string, value?: string) {
		return Url.fromBasePath(`/api/article/property/remove`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			propertyName,
			value,
		});
	}

	public getDraftMergeRequest() {
		return Url.fromBasePath(`/api/mergeRequests/getDraft`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public setMergeRequestApproval(approve: boolean) {
		return Url.fromBasePath(`/api/mergeRequests/setApproval`, this._basePath, {
			catalogName: this._catalogName,
			approve: approve.toString(),
		});
	}

	public uploadStatic() {
		return Url.fromBasePath(`/api/catalog/cloud/upload`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getUploadStatus() {
		return Url.fromBasePath(`/api/catalog/cloud/getUploadStatus`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getPageData(path: string) {
		return Url.fromBasePath(`/api/page/getPageData`, this._basePath, {
			catalogName: this._catalogName,
			path,
		});
	}

	public getScopedPageDataByArticleData(articlePath: string, catalogName: string, scope?: TreeReadScope) {
		return Url.fromBasePath(`/api/page/getScopedPageDataByArticleData`, this._basePath, {
			catalogName,
			scope: scope ? JSON.stringify(scope) : undefined,
			articlePath,
		});
	}

	public getScopedPageDataByCatalog(catalogName: string, scope?: TreeReadScope) {
		return Url.fromBasePath(`/api/page/getScopedPageDataByCatalog`, this._basePath, {
			catalogName,
			scope: scope ? JSON.stringify(scope) : undefined,
		});
	}

	public getInboxArticles(userMail: string) {
		return Url.fromBasePath(`/api/inbox/get`, this._basePath, {
			catalogName: this._catalogName,
			userMail,
		});
	}

	public getInboxUsers() {
		return Url.fromBasePath(`/api/inbox/getUsers`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public createFileInGramaxDir(id: string, type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/create`, this._basePath, {
			id,
			type,
			catalogName: this._catalogName,
		});
	}

	public updateFileInGramaxDir(id: string, type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/update`, this._basePath, {
			id,
			type,
			catalogName: this._catalogName,
		});
	}

	public getFileContentInGramaxDir(id: string, type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/getFileContent`, this._basePath, {
			id,
			type,
			catalogName: this._catalogName,
		});
	}

	public getArticleListInGramaxDir(type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/getItemList`, this._basePath, {
			type,
			catalogName: this._catalogName,
		});
	}

	public removeFileInGramaxDir(id: string, type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/remove`, this._basePath, {
			id,
			type,
			catalogName: this._catalogName,
		});
	}

	public getTemplateProperties(templateId: string) {
		return Url.fromBasePath(`/api/templates/getProperties`, this._basePath, {
			catalogName: this._catalogName,
			templateId,
		});
	}

	public updateTemplateArticleField(field: string) {
		return Url.fromBasePath(`/api/templates/updateArticleField`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			field,
		});
	}

	public removeTemplateArticleField(field: string) {
		return Url.fromBasePath(`/api/templates/removeArticleField`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			field,
		});
	}

	public setSourceState(storageName: string, isValid: boolean) {
		return Url.fromBasePath(`/api/storage/setSourceState`, this._basePath, {
			storageName,
			isValid: isValid.toString(),
		});
	}

	public setSyntax(syntax: Syntax) {
		return Url.fromBasePath(`api/catalog/setSyntax`, this._basePath, {
			catalogName: this._catalogName,
			syntax,
		});
	}

	public reformatCatalog() {
		return Url.fromBasePath(`api/catalog/reformat`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public handleFsEvents() {
		return Url.fromBasePath("/api/fs/handleEvents", this._basePath);
	}

	public getEditTreeInGramaxDir(id: string, type: ArticleProviderType) {
		return Url.fromBasePath(`/api/article/provider/getEditTree`, this._basePath, {
			id,
			type,
			catalogName: this._catalogName,
		});
	}

	public getPrettifiedText(command: string) {
		return Url.fromBasePath(`/api/ai/text/prettify`, this._basePath, {
			command,
			catalogName: this._catalogName,
		});
	}

	public getGeneratedText(command: string) {
		return Url.fromBasePath(`/api/ai/text/generate`, this._basePath, {
			command,
			catalogName: this._catalogName,
		});
	}

	public markArticleAsRead(logicPath: string) {
		return Url.fromBasePath(`/api/article/markAsRead`, this._basePath, {
			catalogName: this._catalogName,
			logicPath,
		});
	}

	public markArticleAsOpened(logicPath: string) {
		return Url.fromBasePath(`/api/article/markAsOpened`, this._basePath, {
			catalogName: this._catalogName,
			logicPath,
		});
	}

	public checkAiAuth(apiUrl: string) {
		return Url.fromBasePath(`/api/ai/server/checkAuth`, this._basePath, {
			apiUrl,
		});
	}

	public checkAiServer(apiUrl: string) {
		return Url.fromBasePath(`/api/ai/server/checkServer`, this._basePath, {
			apiUrl,
		});
	}

	public setAiData(workspacePath: string) {
		return Url.fromBasePath(`/api/ai/server/setData`, this._basePath, {
			workspacePath,
		});
	}

	public removeAiData(workspacePath: string) {
		return Url.fromBasePath(`/api/ai/server/removeData`, this._basePath, {
			workspacePath,
		});
	}

	public getAiData(workspacePath: string) {
		return Url.fromBasePath(`/api/ai/server/getData`, this._basePath, {
			workspacePath,
		});
	}

	moveArticle(
		articlePath: string,
		targetWorkspacePath: WorkspacePath,
		targetCatalogName: string,
		sourceCatalogName: string,
		setName?: string,
		setTitle?: string,
		resolution?: CatalogMoveConflictResolution,
	) {
		return Url.fromBasePath(`/api/article/move`, this._basePath, {
			articlePath,
			targetWorkspacePath,
			targetCatalogName,
			sourceCatalogName,
			setName,
			setTitle,
			resolution,
		});
	}

	public getArticleBacklinks(articlePath: string) {
		return Url.fromBasePath(`/api/catalog/links/backlinks/get`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getArticleLinks(articlePath: string) {
		return Url.fromBasePath(`/api/catalog/links/links/get`, this._basePath, {
			catalogName: this._catalogName,
			articlePath,
		});
	}

	public getComment(id: string, articlePath?: string) {
		return Url.fromBasePath(`/api/comments/get`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: articlePath || this._articlePath,
			id,
		});
	}

	public getAllComments(articlePath?: string) {
		return Url.fromBasePath(`/api/comments/getAll`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: articlePath || this._articlePath,
		});
	}

	public updateComment(id: string, articlePath?: string) {
		return Url.fromBasePath(`/api/comments/update`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: articlePath || this._articlePath,
			id,
		});
	}

	public transcribeAudio() {
		return Url.fromBasePath(`/api/ai/audio/transcribe`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getResourcePath(url: string) {
		return Url.fromBasePath(`/api/article/resource/getPath`, this._basePath, {
			articlePath: this._articlePath,
			catalogName: this._catalogName,
			path: url,
		});
	}

	public getResourcePathByCatalog(path: string) {
		return Url.fromBasePath(`/api/article/resource/getPathByCatalogPath`, this._basePath, {
			catalogName: this._catalogName,
			path,
		});
	}

	public getAnswers() {
		return Url.fromBasePath(`/api/quiz/answers/get`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public getMailSendOTPUrl() {
		return Url.fromBasePath(`/api/auth/mailSendOTP`, this._basePath);
	}

	public getMailLoginOTPUrl() {
		return Url.fromBasePath(`/api/auth/mailLoginOTP`, this._basePath);
	}

	public isAnsweredTest() {
		return Url.fromBasePath(`/api/quiz/getTestResult`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
		});
	}

	public createLinkFromHref(href: string) {
		return Url.fromBasePath(`/api/article/features/createLinkFromHref`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			href,
		});
	}

	public getPathnameToRevision(commitOid: string, oldCommitOid?: string) {
		return Url.fromBasePath(`/api/versionControl/revision/getRevisionPathname`, this._basePath, {
			catalogName: this._catalogName,
			articlePath: this._articlePath,
			commitOid,
			oldCommitOid,
		});
	}

	public getCatalogViews(offset: number, limit: number, search?: string) {
		return Url.fromBasePath(`/api/catalog/views/get`, this._basePath, {
			offset: offset.toString(),
			limit: limit.toString(),
			search: search,
			catalogName: this._catalogName,
		});
	}

	public createCatalogView() {
		return Url.fromBasePath(`/api/catalog/views/create`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public deleteCatalogView(viewId: string) {
		return Url.fromBasePath(`/api/catalog/views/delete`, this._basePath, {
			viewId,
			catalogName: this._catalogName,
		});
	}

	public updateCatalogView() {
		return Url.fromBasePath(`/api/catalog/views/update`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public applyCatalogView(viewId: string, articleLogicPath?: string) {
		return Url.fromBasePath(`/api/catalog/views/apply`, this._basePath, {
			viewId,
			catalogName: this._catalogName,
			articleLogicPath,
		});
	}

	public applyTempCatalogView() {
		return Url.fromBasePath(`/api/catalog/views/applyTemp`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public checkHasCatalogView() {
		return Url.fromBasePath(`/api/catalog/views/check`, this._basePath, {
			catalogName: this._catalogName,
		});
	}

	public getOpenApiAllRefFiles(
		src: string,
		mimeType?: MimeTypes,
		catalogName?: string,
		itemId?: string,
		providerType?: ArticleProviderType,
	) {
		return Url.fromBasePath(`/api/article/resource/openApi/getAllRefFiles`, this._basePath, {
			catalogName: catalogName ?? this._catalogName,
			articlePath: itemId || this._articlePath,
			mimeType,
			src,
			providerType,
		});
	}

	static createFrom(apiUrlCreator: ApiUrlCreator, articlePath: string, providerType: ArticleProviderType) {
		return new ApiUrlCreator(apiUrlCreator._basePath, apiUrlCreator._catalogName, articlePath, providerType);
	}
}
